import { useState, useMemo, useCallback } from 'react';
import { useGetWarehouseQuery, type DashboardOptions } from '@/entities/dashboard';

interface UseStorageOverviewParams {
  initialFilters?: {
    metric?: DashboardOptions['metric'];
    group?: DashboardOptions['group'];
    dateRange?: DashboardOptions['period'];
  };
}

export const useStorageOverview = ({ initialFilters = {} }: UseStorageOverviewParams = {}) => {
  const [options, setOptions] = useState<DashboardOptions>({
    metric: initialFilters.metric || 'W',
    group: initialFilters.group || 'D',
    period: initialFilters.dateRange || 'L6M'
  });

  const { data, isLoading, error, refetch } = useGetWarehouseQuery(options);

  const updateOption = useCallback((key: keyof DashboardOptions, value: string) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const formatValue = useCallback((value: number, metric?: string): string => {
    if (metric === 'W') {
      return `${(value / 1000).toFixed(1)} mt`;
    }
    if (metric === 'V') {
      return `${value.toFixed(1)} m³`;
    }
    if (metric === 'VA') {
      return `$${value.toLocaleString()}`;
    }
    return value.toLocaleString();
  }, []);

  const formattedStats = useMemo(() => {
    if (!data?.stats) return null;

    return {
      totalVolume: formatValue(data.stats.totalVolume, options.metric),
      totalReceived: formatValue(data.stats.totalReceived, options.metric),
      longStandingPerc: `${data.stats.longStandingPerc.toFixed(1)}%`,
      expiringSOH: formatValue(data.stats.expiringSOH, options.metric),
      lossTotal: formatValue(data.stats.lossTotal, options.metric),
      damageTotal: formatValue(data.stats.damageTotal, options.metric)
    };
  }, [data?.stats, options.metric, formatValue]);

  return {
    // Data
    data,
    formattedStats,

    // Loading states
    isLoading,
    error,

    // Filters
    options,

    // Handlers
    updateOption,
    formatValue,
    refetch
  };
};
